import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  Flex,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { getRolePermissions, updateRolePermissions } from '../api/auth';
import { getApiErrorMessage } from '../utils/apiError';

const GroupPermissionsModal = ({ isOpen, onClose, groupName }) => {
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen || !groupName) return;

    let isCancelled = false;

    const fetchPermissions = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await getRolePermissions(groupName);
        if (isCancelled) return;
        setPermissions(
          Array.isArray(data?.permissions) ? data.permissions : []
        );
      } catch (error) {
        if (isCancelled) return;
        console.error('Error fetching group permissions:', error);
        setLoadError(getApiErrorMessage(error, 'Failed to load permissions.'));
        setPermissions([]);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchPermissions();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, groupName]);

  const togglePermission = id => {
    setPermissions(prev =>
      prev.map(permission =>
        permission.id === id
          ? { ...permission, active: !permission.active }
          : permission
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateRolePermissions(groupName, permissions);
      toast({
        title: 'Permissions Updated',
        description: `Permissions for "${groupName}" have been saved.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error('Error updating group permissions:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error, 'Failed to save permissions.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Permissions — {groupName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justifyContent="center" py={6}>
              <Spinner />
            </Flex>
          ) : loadError ? (
            <Text>{loadError}</Text>
          ) : permissions.length === 0 ? (
            <Text>No permissions found.</Text>
          ) : (
            <Flex direction="column" gap={2}>
              {permissions.map(permission => (
                <Checkbox
                  key={permission.id}
                  isChecked={!!permission.active}
                  onChange={() => togglePermission(permission.id)}
                >
                  {permission.description}
                </Checkbox>
              ))}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isLoading || !!loadError}
          >
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GroupPermissionsModal;
